import { Helmet } from 'react-helmet-async';
import { seoConfig } from '@/config/seo';

interface LocalBusinessSchemaProps {
  openingHours?: string[];
  priceRange?: string;
  paymentAccepted?: string[];
  currenciesAccepted?: string[];
  serviceArea?: Array<{
    type: 'State' | 'City' | 'Country';
    name: string;
  }>;
}

export const LocalBusinessSchema: React.FC<LocalBusinessSchemaProps> = ({
  openingHours = ['Mo-Fr 09:00-17:00'],
  priceRange = '$$$',
  paymentAccepted = ['Cash', 'Credit Card', 'Invoice', 'PayPal'],
  currenciesAccepted = ['USD'],
  serviceArea = [
    { type: 'Country', name: 'United States' },
    { type: 'State', name: 'California' },
    { type: 'State', name: 'New York' },
    { type: 'State', name: 'Texas' },
    { type: 'State', name: 'Florida' },
  ],
}) => {
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${seoConfig.site.url}#localbusiness`,
    name: seoConfig.organization.name,
    alternateName: seoConfig.organization.alternateName,
    description: seoConfig.organization.description,
    url: seoConfig.organization.url,
    logo: seoConfig.organization.logo,
    image: [
      seoConfig.organization.logo,
      `${seoConfig.site.url}/og-image.jpg`,
      `${seoConfig.site.url}/office-image.jpg`,
    ],
    telephone: seoConfig.organization.telephone,
    email: seoConfig.organization.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: seoConfig.organization.address.addressLocality,
      addressRegion: seoConfig.organization.address.addressRegion,
      addressCountry: seoConfig.organization.address.addressCountry,
      streetAddress: '123 Technology Drive', // Add actual address
      postalCode: '92501', // Add actual postal code
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '33.9533',
      longitude: '-117.3962',
    },
    openingHoursSpecification: openingHours.map(hours => {
      const [days, time] = hours.split(' ');
      const [startTime, endTime] = time.split('-');
      
      return {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: days.split('-').map(day => {
          const dayMap: { [key: string]: string } = {
            'Mo': 'Monday',
            'Tu': 'Tuesday',
            'We': 'Wednesday',
            'Th': 'Thursday',
            'Fr': 'Friday',
            'Sa': 'Saturday',
            'Su': 'Sunday',
          };
          return dayMap[day] || day;
        }),
        opens: startTime,
        closes: endTime,
      };
    }),
    priceRange,
    paymentAccepted,
    currenciesAccepted,
    areaServed: serviceArea.map(area => ({
      '@type': area.type,
      name: area.name,
    })),
    serviceType: [
      'AI Solutions',
      'NetSuite Development',
      'Shopify Plus Development',
      'Celigo Integration',
      'PowerBI Analytics',
      'Enterprise Software Development',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Technology Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'AI Solutions & Machine Learning',
            description: 'Enterprise AI agents, RAG systems, and machine learning implementations',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'NetSuite ERP Development',
            description: 'Custom NetSuite implementations and SuiteScript development',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Shopify Plus Development',
            description: 'Scalable e-commerce solutions on Shopify Plus platform',
          },
        },
      ],
    },
    sameAs: seoConfig.organization.sameAs,
    foundingDate: seoConfig.organization.foundingDate,
    numberOfEmployees: seoConfig.organization.numberOfEmployees,
    slogan: 'Transforming Business Through Technology Excellence',
    knowsAbout: [
      'Artificial Intelligence',
      'Machine Learning',
      'NetSuite ERP',
      'Shopify Plus',
      'E-commerce Development',
      'Business Intelligence',
      'PowerBI',
      'Celigo Integration',
      'Enterprise Software',
      'Digital Transformation',
    ],
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
    </Helmet>
  );
};